import Link from "next/link";
import cls from "classnames";
import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/nextjs";

import UserAvatar from "@qoohoo/common/components/UserAvatar";
import ShareButtonGroup from "./ShareButtonGroup";
import Description from "./Description";
import ChannelNotAvailable from "./ChannelNotAvailable";
import JoinWhatsappButton from "./JoinWhatsappButton";
import TagGroup from "./TagGroup";
import JoinModals from "../JoinModals";
import CreatorNavBar from "components/Navbar/CreatorNavbar";
import CreatorFooter from "../../CreatorFooter";
import FAQSection from "./FAQSection";

import useSessionStore from "../../../store/sessionStore";
import httpf from "../../../utils/frontend/httpf";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { button, link, pageview } from "@qoohoo/common/constants/analytics";
import useHasHydrated from "@qoohoo/common/hooks/useHasHydrated";
import useStore from "../../../store/store";
import PaymentBanner from "../../premium-content/PaymentBanner";
import Coupon from "../../coupon/Coupon";
import { calcSeatStatus } from "@qoohoo/common/helpers/channel";
import { LimitedSeatsRibbon } from "../../channels/Join";
import { getDurationLabel } from "@qoohoo/common/utils/date";
import MembershipDurationLabel from "../../MembershipDurationLabel";
import { enableNewCheckoutForFeature } from "constants/constants";
import { SuperCheckoutButton } from "../telegram/TelegramPost";


const currentScreen = pageview.channel_details;

const WhatsappPost = ({ channelDetails, isWhiteTheme, customConfig }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [userChannelDetail, setUserChannelDetail] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const loadingStore = useStore((state) => state.loading);

  const isSubscribed = userChannelDetail.is_channel_member;
  const is_private = channelDetails.is_private;

  const isHydrated = useHasHydrated();

  const setReadyToPay = useStore((state) => state.setReadyToPay);
  const userData = useSessionStore((state) => state.userData);
  const country = useStore((state) => state.country);

  const creatorUsernameOrFullname =
    channelDetails.creator?.fullname || channelDetails.creator.username;
  const isVerifiedCreator = channelDetails.creator.is_verified_creator;
  const channelImageUrl = channelDetails.center_image?.url;

  const ctaButtonText =
    userChannelDetail.cta_button?.button_text ||
    channelDetails.cta_button?.button_text;
  const ctaButtonAction =
    userChannelDetail.cta_button?.action || channelDetails.cta_button?.action;
  const isChannelArchived = channelDetails.is_archived;
  const isChannelEnded = channelDetails.is_ended;

  const showMembershipDurationLabel = isSubscribed && !isChannelArchived;

  const isCreator = channelDetails?.creator?.uid === userData?.uid;

  const isCoCreator = useMemo(() => {
    const co_creator_uids = userChannelDetail?.co_creators?.map(
      (creator) => creator?.uid
    );

    return co_creator_uids?.includes(userData?.uid);
  }, [userData?.uid, userChannelDetail.co_creators]);

  const seatStatus =
    !channelDetails.has_limited_seats ||
    ctaButtonAction === null ||
    isSubscribed ||
    isCreator ||
    isCoCreator ||
    isChannelArchived ||
    isChannelEnded
      ? ""
      : calcSeatStatus(
          channelDetails?.max_subscribers,
          channelDetails?.total_seats_left
        );
  const areFewSeatsLeft = seatStatus.includes("left");
  const areSeatsFull = seatStatus.includes("No seats");

  const isLifetime = userChannelDetail.duration?.toLowerCase() === "lifetime";
  const isOneTime = channelDetails.type.toLowerCase() === "one_time";

  const showCouponField =
    !isChannelEnded &&
    !isChannelArchived &&
    channelDetails?.is_paid &&
    !isCreator &&
    !isCoCreator &&
    (isSubscribed ? !(isLifetime || isOneTime) : !areSeatsFull);

  const autoJoinSettingDisabled =
    userChannelDetail.whatsapp?.auto_join_setting_disabled;

  const trackDetails = {
    ...channelDetails,
    current_screen: currentScreen,
    is_whatsapp: true,
    uid: channelDetails.uid,
    "creator.uid": channelDetails.creator.uid,
    "creator.full_name": channelDetails.creator.fullname,
    "creator.username": channelDetails.creator.username,
    is_verified_creator: channelDetails.creator.is_verified_creator,
    channel_status: ctaButtonText,
    currentUser: userData?.uid,
  };

  function handleButtonClick() {
    if (!userData?.uid) {
      setModalOpen(true);
    } else {
      const hasMultiplePlans = channelDetails?.plans?.length > 1;

      if (hasMultiplePlans) {
        setModalOpen(true);
      } else {
        setModalOpen(true);
        setReadyToPay(true);
      }
    }

    Track.buttonClick(button.cta_btn, trackDetails);
  }

  const prepareCouponResponse = (coupon) => {
    return {
      coupon,
      channel: channelDetails.uid,
    };
  };

  const onCouponValidation = (data) => {
    setCouponState(data);
  };

  const [couponState, setCouponState] = useState({});

  function getUserChannelDetail() {
    setIsLoading(true);
    httpf
      .post(`/api/auth-channel-details`, {
        id: channelDetails.uid,
      })
      .then((res) => {
        setUserChannelDetail(res.data);
      })
      .catch((err) => {
        console.error(err);
        Sentry.captureException(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function renderJoinButton(isFooter = false) {
    const enableNewCheckout = enableNewCheckoutForFeature.whatsapp_channel;

    let joinButton = null;

    if (isSubscribed && (isLifetime || isOneTime)) {
      joinButton = null;
    } else if (isCreator || isCoCreator) {
      if (isFooter) {
        joinButton = null;
      } else {
        joinButton = (
          <div className="mt-6 pt-6 text-left border-t border-[#2B353B]">
            <p className="text-xs leading-[150%]">
              You're the creator of this WhatsApp group.
            </p>
          </div>
        );
      }
    } else {
      joinButton = (
        <div
          className={cls(
            "flex-col items-center text-center z-[1]",
            isFooter
              ? "sm:hidden flex sticky bottom-0 w-full px-7 pb-10 pt-4 bg-[#262C30] dark:bg-white dark:border border-[#D5D8DC]"
              : "hidden sm:flex sm:relative sm:w-[fit-content] sm:mx-auto sm:py-0 sm:px-0 sm:bg-transparent sm:mt-6"
          )}
        >
          {enableNewCheckout ? (
          <SuperCheckoutButton
            className="!rounded-full sm:w-80 bg-none bg-[#128C7E]"
            channelDetails={channelDetails}
            userChannelDetail={userChannelDetail}
            userData={userData}
            customConfig={customConfig}
            isLoading={!isHydrated || isLoading || loadingStore}
            isDisabled={
              areSeatsFull && !isSubscribed && !isCreator && !isCoCreator
            }
          />
          ) : (
            <>
            {showCouponField && (
              <Coupon
                state={couponState}
                prepareResponse={prepareCouponResponse}
                onValidation={onCouponValidation}
                className="mb-4"
              />
            )}
            <JoinWhatsappButton
              onClick={handleButtonClick}
              channelDetails={channelDetails}
              userChannelDetail={userChannelDetail}
              isLoading={!isHydrated || isLoading || loadingStore}
              couponState={couponState}
              isDisabled={
                areSeatsFull && !isSubscribed && !isCoCreator && !isCreator
              }
            />
            </>
          )}
        </div>
      );
    }

    return joinButton;
  }

  useEffect(() => {
    if (userData?.uid) {
      getUserChannelDetail();
    }
  }, [userData]);

  if (isChannelArchived || isChannelEnded) {
    const creatorProfileLink = `/by/${channelDetails.creator.username}`;
    return (
      <>
        <div className={cls("w-full sm:mb-0 min-h-screen flex flex-col justify-between items-stretch", {"dark": isWhiteTheme})}>
          <div>
            <CreatorNavBar creator={channelDetails.creator} currentScreen={currentScreen} isDark={!isWhiteTheme}/>
            <ChannelNotAvailable creatorProfileLink={creatorProfileLink} />
          </div>
          <CreatorFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={cls(
          "w-full sm:mb-0 min-h-screen flex flex-col justify-between items-stretch",
          {"dark": isWhiteTheme}
        )}
      >
        <div className="dark:bg-[#F5F5F5]">
          <CreatorNavBar creator={channelDetails.creator} currentScreen={currentScreen} isDark={!isWhiteTheme}/>

          <main className="sm:mt-5 mb-20 sm:mb-44 mx-auto text-white dark:bg-[#F5F5F5]">
            <MembershipDurationLabel
              isHeaderMode={true}
              isVisible={showMembershipDurationLabel}
              userChannelDetail={userChannelDetail}
              link={userChannelDetail.whatsapp?.invite_link}
              linkLabel="Join on WhatsApp"
              colFormat={true}
            />
            <div className="relative w-auto mx-auto sm:w-auto">
              <div className="relative mx-auto px-6 pt-11 sm:p-9 sm:pb-6 w-full sm:w-[484px] sm:border sm:border-[#2b353b] sm:bg-[#161a1d] sm:rounded-2xl dark:sm:bg-[#FFFFFF] dark:border-[#D5D8DC]">
                <LimitedSeatsRibbon seatStatus={seatStatus} />
                <MembershipDurationLabel
                  isHeaderMode={false}
                  isVisible={showMembershipDurationLabel}
                  userChannelDetail={userChannelDetail}
                  link={userChannelDetail.whatsapp?.invite_link}
                  linkLabel="Join on WhatsApp"
                  colFormat={true}
                />
                <div
                  className={cls(
                    "flex flex-col sm:flex-row space-y-5 sm:space-y-0 sm:space-x-8 items-center",
                    {
                      "mt-5": !!seatStatus,
                    }
                  )}
                >
                  {!!channelImageUrl && (
                    <div className="relative max-h-[148px] sm:max-h-28 max-w-[148px] sm:min-w-[112px] w-[148px] sm:w-28">
                      <img
                        src={channelDetails.center_image.url}
                        className="rounded-[28px] overflow-hidden w-full h-full object-cover"
                        alt={channelDetails.name}
                      />
                    </div>
                  )}
                  <div
                    className={cls(
                      "self-start sm:self-auto space-y-6 sm:space-y-4 max-w-full",
                      !!channelImageUrl && "max-w-full sm:max-w-[268px]"
                    )}
                  >
                    <h1
                      className={cls(
                        "font-bold text-2xl leading-[150%] whitespace-pre-wrap line-clamp-3 dark:text-[#16191B]",
                        !!channelImageUrl && "sm:max-w-[260px]"
                      )}
                    >
                      {channelDetails.name}
                    </h1>
                    <Link
                      href={`/by/${channelDetails.creator.username}`}
                      passHref
                      target="_blank"
                      className="flex cursor-pointer max-w-full"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="flex justify-center items-center text-sm font-semibold object-cover min-w-[28px] sm:min-w-[24px] w-7 sm:w-6 h-7 sm:h-6 overflow-hidden rounded-full bg-[#2b3135]">
                          <UserAvatar
                            avatarUrl={channelDetails.creator.avatar?.url}
                            userName={creatorUsernameOrFullname}
                          />
                        </div>
                        <div
                          className={cls(
                            "flex items-center text-sm leading-4 capitalize max-w-[85%] dark:text-[#16191B]"
                          )}
                        >
                          <span className={cls("truncate sm:max-w-xs")}>
                            {creatorUsernameOrFullname}
                          </span>
                          {isVerifiedCreator && (
                            <img
                              src="/assets/icons/round-verified.svg"
                              alt="verified icon"
                              className="w-5 h-5 ml-2"
                            />
                          )}
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>

                <TagGroup channelDetails={channelDetails} />

                <Description
                  text={channelDetails.description}
                  className="text-sm leading-5 mt-6"
                  trackDetails={trackDetails}
                />

                {renderJoinButton()}

                {/* Share Button Group */}
                {isSubscribed && (
                  <ShareButtonGroup
                    channelName={channelDetails.name}
                    channelType={"group"}
                    shareUrl={channelDetails.short_url_flutter}
                    creatorDetails={channelDetails.creator}
                    trackDetails={trackDetails}
                    isWhiteTheme={isWhiteTheme}
                  />
                )}
              </div>

              {/* Payment Banner */}
              {!isSubscribed && (
                <div className="pt-5 pb-1 md:p-0">
                  <PaymentBanner country={country} />
                </div>
              )}
            </div>

            <FAQSection
              channelDetails={channelDetails}
              isWhiteTheme={isWhiteTheme}
              customConfig={customConfig}
            />
          </main>

          {modalOpen ? (
            <JoinModals
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              channelDetails={channelDetails}
              userChannelDetail={userChannelDetail}
              isSubscribed={isSubscribed}
              isPrivate={is_private}
              isCreator={isCreator}
              isCoCreator={isCoCreator}
              getUserChannelDetail={getUserChannelDetail}
              couponState={couponState}
              onCouponValidation={onCouponValidation}
              prepareCouponResponse={prepareCouponResponse}
              setIsLoading={setIsLoading}
              isLoadingApiResponse={isLoading}
              isWhiteTheme={isWhiteTheme}
            />
          ) : null}
        </div>
        <CreatorFooter isWhiteTheme={isWhiteTheme}/>
        {renderJoinButton(true)}
      </div>
    </>
  );
};

export default WhatsappPost;